const React = require("react");
const PropTypes = require("prop-types");
require("./FAQ.scss");

// Fast dash trimming without regex: linear scan of code points
const DASH_CODES = new Set([
  0x002d, // - hyphen-minus
  0x2010, // ‐ hyphen
  0x2011, // ‑ non-breaking hyphen
  0x2012, // ‒ figure dash
  0x2013, // – en dash
  0x2014, // — em dash
  0x2015, // ― horizontal bar
  0x2043, // ⁃ hyphen bullet
  0x2212, // − minus sign
  0xfe58, // ﹘ small em dash
  0xfe63, // ﹣ small hyphen-minus
  0xff0d, // － fullwidth hyphen-minus
]);

function isDashCode(code) {
  return DASH_CODES.has(code);
}

function trimHyphens(str) {
  const s = String(str || "");
  let start = 0;
  let end = s.length;
  // Unicode-aware forward scan (by code points)
  while (start < end) {
    const cp = s.codePointAt(start);
    if (!isDashCode(cp)) break;
    start += cp > 0xffff ? 2 : 1;
  }
  // Unicode-aware backward scan (by code points)
  while (end > start) {
    // Use codePointAt to handle surrogate pairs properly
    const lastUnitCp = s.codePointAt(end - 1);
    // If the last unit is a low surrogate, step back two units to reach the code point start
    const cpStart =
      lastUnitCp >= 0xdc00 && lastUnitCp <= 0xdfff ? end - 2 : end - 1;
    const cp = s.codePointAt(cpStart);
    if (!isDashCode(cp)) break;
    end = cpStart;
  }
  return start === 0 && end === s.length ? s : s.slice(start, end);
}

function slugify(input) {
  try {
    return trimHyphens(
      String(input || "")
        .normalize("NFD")
        .replaceAll(/\p{Diacritic}+/gu, "")
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
    );
  } catch {
    return trimHyphens(
      String(input || "")
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, "-")
    );
  }
}

function extractText(node) {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(" ");
  if (React.isValidElement(node)) return extractText(node.props?.children);
  return "";
}

const FAQ = ({ children, className = "" }) => {
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const root = ref.current;
    if (!root) return;
    const detailsList = Array.from(root.querySelectorAll("details.faq-item"));

    const openOnly = (target) => {
      for (const d of detailsList) {
        if (d !== target) d.removeAttribute("open");
      }
    };

    const onToggle = (e) => {
      const t = e.target;
      if (t?.tagName !== "DETAILS") return;
      if (t.open) {
        openOnly(t);
        const id = t.id;
        try {
          // Only update URL hash on user-triggered interactions to
          // avoid auto-appending a hash on initial programmatic open.
          if (e.isTrusted) {
            const hash = id ? `#${id}` : "";
            if (
              hash &&
              typeof window !== "undefined" &&
              window.history?.replaceState
            ) {
              window.history.replaceState(null, "", hash);
            }
          }
        } catch {}
      }
    };
    root.addEventListener("toggle", onToggle, true);

    // Deep-linking: open from hash
    try {
      const h = (
        (typeof window !== "undefined" && window.location?.hash) ||
        ""
      ).slice(1);
      if (h) {
        const target = root.querySelector(
          `details#${CSS?.escape ? CSS.escape(h) : h}`
        );
        if (target) {
          target.setAttribute("open", "open");
          openOnly(target);
          try {
            target.scrollIntoView({ block: "center" });
          } catch {}
        }
      } else {
        // No hash: do not auto-open any item by default.
      }
    } catch {}

    return () => root.removeEventListener("toggle", onToggle, true);
  }, []);

  // Build FAQPage JSON-LD from children
  const entities = React.Children.toArray(children)
    .map((child) => {
      if (!React.isValidElement(child)) return null;
      const type = child.type;
      const isItem =
        type && (type === FAQItem || type?.displayName === "FAQItem");
      if (!isItem) return null;
      const question = child.props?.question || "";
      const acceptedAnswerText = extractText(child.props?.children);
      return question
        ? {
            "@type": "Question",
            name: String(question),
            acceptedAnswer: { "@type": "Answer", text: acceptedAnswerText },
          }
        : null;
    })
    .filter(Boolean);

  const ld = entities.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: entities,
      }
    : null;

  return React.createElement(
    React.Fragment,
    null,
    React.createElement(
      "div",
      { ref, className: ["faq", className].filter(Boolean).join(" ") },
      children
    ),
    ld &&
      React.createElement("script", {
        type: "application/ld+json",
        dangerouslySetInnerHTML: { __html: JSON.stringify(ld) },
      })
  );
};

const FAQItem = ({ question, children, open = false, id }) => {
  const computedId = id || (question ? `faq-${slugify(question)}` : undefined);
  return React.createElement(
    "details",
    { className: "faq-item", open, id: computedId },
    [
      React.createElement(
        "summary",
        { className: "faq-question", role: "button", "aria-expanded": open },
        [
          question,
          React.createElement(
            "span",
            { className: "faq-icon", "aria-hidden": true },
            "▸"
          ),
        ]
      ),
      React.createElement("div", { className: "faq-answer" }, children),
    ]
  );
};

FAQItem.displayName = "FAQItem";

FAQ.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

FAQItem.propTypes = {
  question: PropTypes.string.isRequired,
  children: PropTypes.node,
  open: PropTypes.bool,
  id: PropTypes.string,
};

module.exports = { FAQ, FAQItem, default: FAQ };
